/**
 * Credential kind — normalized types for all integrations.
 * Tools declare which kind(s) they need; the orchestrator checks
 * the credential vault against these requirements.
 */
export type CredentialKind = 'oauth' | 'api_key' | 'api_key_pair' | 'password' | 'webhook';

/**
 * Typed credential requirement for a single tool/integration.
 */
export interface ToolCredentialRequirement {
  kind: CredentialKind;
  /** The credential name (matches a key in CREDENTIAL_REQUIREMENTS) */
  name: string;
  /** Optional scopes for OAuth-type credentials */
  scopes?: string[];
}

/**
 * Maps tool name prefixes to their integration key + credential requirements.
 * The orchestrator resolves tools → this registry → credential vault.
 *
 * Format: tool prefix → { integration key, credential requirements[] }
 *
 * When a skill declares `depends-on-tools: [github-repo-manager]`, the
 * orchestrator finds matching prefix entries here and checks credentials.
 */
export const TOOL_INTEGRATION_REGISTRY: Record<
  string,
  {
    integration: string;
    requires_credentials: ToolCredentialRequirement[];
  }
> = {
  github_: {
    integration: 'github',
    requires_credentials: [{ kind: 'api_key', name: 'github', scopes: ['repo', 'workflow'] }],
  },
  tangled_: {
    integration: 'tangled',
    requires_credentials: [{ kind: 'api_key', name: 'tangled' }],
  },
  trello_: {
    integration: 'trello',
    requires_credentials: [{ kind: 'api_key_pair', name: 'trello' }],
  },
  notion_: {
    integration: 'notion',
    requires_credentials: [{ kind: 'api_key', name: 'notion' }],
  },
  slack_: {
    integration: 'slack',
    requires_credentials: [{ kind: 'oauth', name: 'slack' }],
  },
  discord_webhook: {
    integration: 'discord',
    requires_credentials: [{ kind: 'webhook', name: 'discord' }],
  },
  kalshi_: {
    integration: 'kalshi',
    requires_credentials: [{ kind: 'api_key', name: 'kalshi' }],
  },
  promptbase_: {
    integration: 'promptbase',
    requires_credentials: [{ kind: 'password', name: 'promptbase' }],
  },
  stripe_: {
    integration: 'stripe',
    requires_credentials: [{ kind: 'api_key', name: 'stripe' }],
  },
  adult_: {
    integration: 'adult',
    requires_credentials: [{ kind: 'api_key', name: 'adult' }],
  },
  twitter_: {
    integration: 'twitter',
    requires_credentials: [
      { kind: 'oauth', name: 'twitter', scopes: ['tweet.read', 'tweet.write', 'users.read'] },
    ],
  },
  instagram_: {
    integration: 'instagram',
    requires_credentials: [
      {
        kind: 'oauth',
        name: 'instagram',
        scopes: ['instagram_basic', 'instagram_content_publish'],
      },
    ],
  },
  facebook_: {
    integration: 'facebook',
    requires_credentials: [
      { kind: 'oauth', name: 'facebook', scopes: ['pages_manage_posts', 'pages_read_engagement'] },
    ],
  },
  linkedin_: {
    integration: 'linkedin',
    requires_credentials: [
      { kind: 'oauth', name: 'linkedin', scopes: ['w_member_social', 'r_liteprofile'] },
    ],
  },
  social_post_all: {
    integration: 'social_media',
    requires_credentials: [],
  },
  // Media generation tools use server-side ML_API_KEY / HF_TOKEN — no user credentials needed.
  // Listing them here prevents the generic fallback from prompting users for api_key / HF_TOKEN.
  generate_image: {
    integration: 'ml_media',
    requires_credentials: [],
  },
  generate_video: {
    integration: 'ml_media',
    requires_credentials: [],
  },
  generate_voice: {
    integration: 'ml_media',
    requires_credentials: [],
  },
  generate_sound: {
    integration: 'ml_media',
    requires_credentials: [],
  },
  generate_music: {
    integration: 'ml_media',
    requires_credentials: [],
  },
  generate_3d: {
    integration: 'ml_media',
    requires_credentials: [],
  },
  // Web search/scrape uses server-side EXA_SEARCH_API_KEY — no user credentials needed.
  web_search: {
    integration: 'web_tools',
    requires_credentials: [],
  },
  browse_page: {
    integration: 'web_tools',
    requires_credentials: [],
  },
  web_scrape: {
    integration: 'web_tools',
    requires_credentials: [],
  },
  http_get: {
    integration: 'web_tools',
    requires_credentials: [],
  },
  http_post: {
    integration: 'web_tools',
    requires_credentials: [],
  },
};

/**
 * Resolve which integrations a list of tool names requires.
 * Returns a unique set of integration keys.
 */
export function resolveToolIntegrations(toolNames: string[]): string[] {
  const integrations = new Set<string>();
  for (const toolName of toolNames) {
    for (const [prefix, entry] of Object.entries(TOOL_INTEGRATION_REGISTRY)) {
      if (toolName.startsWith(prefix) || toolName === prefix.replace(/_$/, '')) {
        integrations.add(entry.integration);
      }
    }
  }
  return Array.from(integrations);
}

/**
 * Check if all required credentials for a set of tools are present.
 * Returns { ready: true } or { ready: false, missing: [...] }.
 */
export function checkToolCredentials(
  toolNames: string[],
  credentials: Record<string, any>,
): { ready: boolean; missing: Array<{ integration: string; label: string; fields: string[] }> } {
  const missing: Array<{ integration: string; label: string; fields: string[] }> = [];
  const integrations = resolveToolIntegrations(toolNames);

  for (const integrationKey of integrations) {
    const req = CREDENTIAL_REQUIREMENTS[integrationKey];
    if (!req) continue;

    const saved = credentials[integrationKey];
    const optionalFields = new Set(['database_id']);

    if (!saved) {
      // Entire integration missing
      const requiredFields = req.fields.filter((f) => !optionalFields.has(f.key));
      missing.push({
        integration: integrationKey,
        label: req.label,
        fields: requiredFields.map((f) => f.key),
      });
      continue;
    }

    // Check individual required fields
    const missingFields = req.fields
      .filter((f) => !optionalFields.has(f.key))
      .filter((f) => !saved[f.key]);

    if (missingFields.length > 0) {
      missing.push({
        integration: integrationKey,
        label: req.label,
        fields: missingFields.map((f) => f.key),
      });
    }
  }

  return { ready: missing.length === 0, missing };
}

/**
 * Defines what credentials are required for each integration
 */
export const CREDENTIAL_REQUIREMENTS: Record<
  string,
  {
    label: string;
    fields: { key: string; label: string; type: 'text' | 'password'; placeholder: string }[];
  }
> = {
  slack: {
    label: 'Slack',
    fields: [
      {
        key: 'bot_token',
        label: 'Bot Token',
        type: 'password',
        placeholder: 'xoxb-... (Slack app bot token)',
      },
    ],
  },
  github: {
    label: 'GitHub',
    fields: [
      {
        key: 'token',
        label: 'Personal Access Token',
        type: 'password',
        placeholder: 'ghp_... (GitHub Personal Access Token)',
      },
    ],
  },
  tangled: {
    label: 'Tangled',
    fields: [
      {
        key: 'token',
        label: 'Access Token',
        type: 'password',
        placeholder: 'Tangled API access token (if needed)',
      },
      {
        key: 'base_url',
        label: 'Base URL (Optional)',
        type: 'text',
        placeholder: 'https://tangled.org',
      },
    ],
  },
  // clawdhub + openclaw use global server secrets (CLHUB_API_KEY, GITHUB_TOKEN), not per-user
  notion: {
    label: 'Notion',
    fields: [
      {
        key: 'api_key',
        label: 'Integration Token',
        type: 'password',
        placeholder: 'secret_... (Notion integration token)',
      },
      {
        key: 'database_id',
        label: 'Database ID (Optional)',
        type: 'text',
        placeholder: 'Default database ID (optional)',
      },
    ],
  },
  kalshi: {
    label: 'Kalshi',
    fields: [
      {
        key: 'accessKey',
        label: 'Access Key',
        type: 'password',
        placeholder: 'Your Kalshi API access key',
      },
    ],
  },
  trello: {
    label: 'Trello',
    fields: [
      {
        key: 'api_key',
        label: 'API Key',
        type: 'text',
        placeholder: 'Your Trello API key',
      },
      {
        key: 'token',
        label: 'Token',
        type: 'password',
        placeholder: 'Your Trello token',
      },
    ],
  },
  discord: {
    label: 'Discord',
    fields: [
      {
        key: 'webhook_url',
        label: 'Webhook URL',
        type: 'password',
        placeholder: 'https://discordapp.com/api/webhooks/...',
      },
    ],
  },
  promptbase: {
    label: 'Promptbase',
    fields: [
      {
        key: 'pb_username',
        label: 'Promptbase Username / Email',
        type: 'text',
        placeholder: 'your-email@example.com',
      },
      {
        key: 'pb_password',
        label: 'Promptbase Password',
        type: 'password',
        placeholder: 'Your Promptbase password',
      },
    ],
  },
  twitter: {
    label: 'Twitter / X',
    fields: [
      {
        key: 'bearer_token',
        label: 'Bearer Token (API v2)',
        type: 'password',
        placeholder: 'AAAA... (Twitter API v2 Bearer Token)',
      },
    ],
  },
  instagram: {
    label: 'Instagram',
    fields: [
      {
        key: 'access_token',
        label: 'Facebook/Instagram Access Token',
        type: 'password',
        placeholder: 'Long-lived Facebook access token with instagram_content_publish scope',
      },
      {
        key: 'ig_user_id',
        label: 'Instagram Business Account ID',
        type: 'text',
        placeholder: 'Your Instagram Business Account numeric ID',
      },
    ],
  },
  facebook: {
    label: 'Facebook Page',
    fields: [
      {
        key: 'page_access_token',
        label: 'Page Access Token',
        type: 'password',
        placeholder: 'Facebook Page access token with pages_manage_posts permission',
      },
      {
        key: 'page_id',
        label: 'Page ID',
        type: 'text',
        placeholder: 'Your Facebook Page numeric ID',
      },
    ],
  },
  linkedin: {
    label: 'LinkedIn',
    fields: [
      {
        key: 'access_token',
        label: 'OAuth Access Token',
        type: 'password',
        placeholder: 'LinkedIn OAuth 2.0 access token with w_member_social scope',
      },
      {
        key: 'person_urn',
        label: 'Person URN',
        type: 'text',
        placeholder: 'urn:li:person:abc123 (your LinkedIn member URN)',
      },
    ],
  },
  google_meet: {
    label: 'Google Meet (Meeting Bot)',
    fields: [
      {
        key: 'email',
        label: 'Google Account Email',
        type: 'text',
        placeholder: 'meetingbot@yourcompany.com',
      },
      {
        key: 'password',
        label: 'Google Account Password',
        type: 'password',
        placeholder: 'Account password (use an app password if 2FA enabled)',
      },
    ],
  },
  microsoft_teams: {
    label: 'Microsoft Teams (Meeting Bot)',
    fields: [
      {
        key: 'email',
        label: 'Microsoft Account Email',
        type: 'text',
        placeholder: 'meetingbot@yourcompany.com',
      },
      {
        key: 'password',
        label: 'Microsoft Account Password',
        type: 'password',
        placeholder: 'Account password',
      },
    ],
  },
  zoom: {
    label: 'Zoom (Meeting Bot)',
    fields: [
      {
        key: 'email',
        label: 'Zoom Account Email',
        type: 'text',
        placeholder: 'meetingbot@yourcompany.com',
      },
      {
        key: 'password',
        label: 'Zoom Account Password',
        type: 'password',
        placeholder: 'Account password',
      },
    ],
  },
  webex: {
    label: 'WebEx (Meeting Bot)',
    fields: [
      {
        key: 'email',
        label: 'WebEx Account Email',
        type: 'text',
        placeholder: 'meetingbot@yourcompany.com',
      },
      {
        key: 'password',
        label: 'WebEx Account Password',
        type: 'password',
        placeholder: 'Account password',
      },
    ],
  },
};

export interface MissingCredentialError {
  type: 'MISSING_CREDENTIALS';
  integration: string;
  fields: string[];
  message: string;
}

export function createMissingCredentialError(
  integration: string,
  missingFields?: string[],
): MissingCredentialError {
  const req = CREDENTIAL_REQUIREMENTS[integration];
  const fields = missingFields || req?.fields.map((f) => f.key) || [];

  return {
    type: 'MISSING_CREDENTIALS',
    integration,
    fields,
    message: `${req?.label || integration} credentials required. Missing: ${fields.join(', ')}`,
  };
}

export function isMissingCredentialError(error: any): error is MissingCredentialError {
  return error?.type === 'MISSING_CREDENTIALS';
}
